import { useCallback, useEffect, useRef, useState } from 'react';
import axios, { AxiosResponse } from 'axios';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const AUTH_TOKEN_KEY = 'nadiAstroToken';

type PremiumPopupState = {
  open: boolean;
  featureKey: string | null;
  featureName: string;
  used: number;
  total: number;
  remaining: number;
  nextAction: (() => Promise<void>) | null;
};

type CheckAccessArgs<T> = {
  featureKey: string;
  apiCall: (trialAccessToken?: string) => Promise<AxiosResponse<T>>;
  onSuccess?: (data: T | (T & { trialInfo?: any })) => void;
};

const initialPopup: PremiumPopupState = {
  open: false,
  featureKey: null,
  featureName: '',
  used: 0,
  total: 4,
  remaining: 0,
  nextAction: null
};

const showDefaultPremiumLock = (data: any) => {
  window.alert(
    data?.message ||
      'Your 3-day trial has ended. Please choose a Kundlistar Studio plan to continue.'
  );
};

export function usePremiumFeatureAccess({ onShowPremiumLock }: { onShowPremiumLock?: (data: any) => void } = {}) {
  const [popup, setPopup] = useState<PremiumPopupState>(initialPopup);
  const [loading, setLoading] = useState(false);
  const onShowPremiumLockRef = useRef(onShowPremiumLock);

  useEffect(() => {
    onShowPremiumLockRef.current = onShowPremiumLock;
  }, [onShowPremiumLock]);

  const closePopup = useCallback(() => {
    setPopup(initialPopup);
  }, []);

  const consumeCreditAndRetry = useCallback(async <T,>({
    featureKey,
    apiCall,
    onSuccess
  }: CheckAccessArgs<T>) => {
    try {
      setLoading(true);
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const consumeResponse = await axios.post(`${API_BASE_URL}/premium-trial/consume`, { featureKey }, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!consumeResponse.data?.success) {
        if (consumeResponse.data?.showPremiumLock) {
          if (onShowPremiumLockRef.current) onShowPremiumLockRef.current(consumeResponse.data);
          else showDefaultPremiumLock(consumeResponse.data);
          closePopup();
          return;
        }
      }

      closePopup();
      const response = await apiCall(consumeResponse.data?.trialAccessToken);

      if (response.data) {
        onSuccess?.({
          ...(response.data as T),
          trialInfo: consumeResponse.data
        });
      }
    } catch (error: any) {
      const data = error.response?.data;

      if (data?.showPremiumLock) {
        if (onShowPremiumLockRef.current) onShowPremiumLockRef.current(data);
        else showDefaultPremiumLock(data);
        closePopup();
        return;
      }

      window.alert(data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [closePopup]);

  const checkFeatureAccess = useCallback(async <T,>({
    featureKey,
    apiCall,
    onSuccess
  }: CheckAccessArgs<T>) => {
    try {
      const response = await apiCall();

      if (response.data) {
        onSuccess?.(response.data as T);
      }
    } catch (error: any) {
      const data = error.response?.data;

      if (data?.code === 'PREMIUM_CONFIRM_REQUIRED') {
        setPopup({
          open: true,
          featureKey: data.featureKey,
          featureName: data.featureName,
          used: data.trialCreditsUsed,
          total: data.trialCreditsTotal,
          remaining: data.trialCreditsRemaining,
          nextAction: async () => {
            await consumeCreditAndRetry({ featureKey: data.featureKey, apiCall, onSuccess });
          }
        });
        return;
      }

      if (data?.code === 'TRIAL_CREDITS_FINISHED' || data?.code === 'PREMIUM_REQUIRED' || data?.code === 'PLAN_REQUIRED') {
        if (onShowPremiumLockRef.current) onShowPremiumLockRef.current(data);
        else showDefaultPremiumLock(data);
        return;
      }

      throw error;
    }
  }, [consumeCreditAndRetry]);

  return {
    popup,
    loading,
    checkFeatureAccess,
    closePopup
  };
}
